import { Buildings, Users, Stack, Heartbeat, ArrowClockwise } from '@phosphor-icons/react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { StatusDot } from '@/components/StatusDot'
import { useSystemStatus, toStatusDotStatus } from '@/hooks/useSystemStatus'
import type { SystemStatusResponse } from '@/hooks/useSystemStatus'

// ---------- Helpers ----------

function getPlatformStatus(infra: SystemStatusResponse['infrastructure']): {
  label: string
  status: 'healthy' | 'degraded' | 'down' | 'unknown'
} {
  const statuses = [infra.database, infra.redis, infra.weaviate, infra.scheduler]
  const allHealthy = statuses.every(
    (s) => s.status === 'healthy' || s.status === 'running'
  )
  const anyDown = statuses.some(
    (s) => s.status === 'unhealthy'
  )

  if (allHealthy) return { label: 'All Systems Operational', status: 'healthy' }
  if (anyDown) return { label: 'Service Disruption', status: 'down' }
  return { label: 'Partial Degradation', status: 'degraded' }
}

// ---------- Components ----------

function StatCard({
  label,
  value,
  icon: Icon,
  subtext,
}: {
  label: string
  value: string | number
  icon: React.ElementType
  subtext?: string
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-semibold tracking-tight">{value}</div>
        {subtext && (
          <p className="mt-1 text-xs text-muted-foreground">{subtext}</p>
        )}
      </CardContent>
    </Card>
  )
}

function StatCardSkeleton() {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-4" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-8 w-16" />
        <Skeleton className="mt-2 h-3 w-32" />
      </CardContent>
    </Card>
  )
}

function InfraItem({
  label,
  status,
  detail,
}: {
  label: string
  status: string
  detail?: string
}) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
      <StatusDot status={toStatusDotStatus(status)} />
      <span className="text-sm font-medium">{label}</span>
      {detail && (
        <span className="text-xs text-muted-foreground">{detail}</span>
      )}
    </div>
  )
}

function InfraItemSkeleton() {
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
      <Skeleton className="h-2.5 w-2.5 rounded-full" />
      <Skeleton className="h-4 w-16" />
      <Skeleton className="h-3 w-12" />
    </div>
  )
}

// ---------- Dashboard ----------

export function Dashboard() {
  const { data, isLoading, isError, error, refetch } = useSystemStatus()

  if (isError) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Dashboard</h2>
          <p className="text-sm text-muted-foreground">Platform overview and system health</p>
        </div>
        <Card className="border-destructive/50">
          <CardContent className="flex flex-col items-center gap-4 py-10">
            <p className="text-sm text-destructive">
              Failed to load platform status: {error instanceof Error ? error.message : 'Unknown error'}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
            >
              <ArrowClockwise className="mr-2 h-4 w-4" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const platformStatus = data ? getPlatformStatus(data.infrastructure) : null

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Dashboard</h2>
        <p className="text-sm text-muted-foreground">Platform overview and system health</p>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {isLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : data ? (
          <>
            <StatCard
              label="Total Organizations"
              value={data.platform.organizations.length}
              icon={Buildings}
              subtext={`${data.platform.organizations.filter(o => o.is_active).length} active`}
            />
            <StatCard
              label="Total Users"
              value={data.platform.total_users}
              icon={Users}
              subtext={`across ${data.platform.organizations.length} organizations`}
            />
            <StatCard
              label="Active Verticals"
              value={data.platform.verticals.filter(v => v.is_active).length}
              icon={Stack}
              subtext={`${data.platform.verticals.length} total configured`}
            />
            <StatCard
              label="Platform Status"
              value={platformStatus?.label ?? 'Unknown'}
              icon={Heartbeat}
              subtext={data.infrastructure.database.latency_ms
                ? `DB latency: ${data.infrastructure.database.latency_ms}ms`
                : undefined}
            />
          </>
        ) : null}
      </div>

      {/* Infrastructure Status Row */}
      <div>
        <h3 className="mb-3 text-sm font-medium text-muted-foreground">Infrastructure Health</h3>
        <div className="flex flex-wrap gap-3">
          {isLoading ? (
            <>
              <InfraItemSkeleton />
              <InfraItemSkeleton />
              <InfraItemSkeleton />
              <InfraItemSkeleton />
            </>
          ) : data ? (
            <>
              <InfraItem
                label="Database"
                status={data.infrastructure.database.status}
                detail={
                  data.infrastructure.database.latency_ms
                    ? `${data.infrastructure.database.latency_ms}ms`
                    : undefined
                }
              />
              <InfraItem
                label="Cache"
                status={data.infrastructure.redis.status}
              />
              <InfraItem
                label="Vector DB"
                status={data.infrastructure.weaviate.status}
              />
              <InfraItem
                label="Scheduler"
                status={data.infrastructure.scheduler.status}
              />
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}
